import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Archive, CheckCircle2, Copy, Pencil, Plus } from "lucide-react";
import { rhm, apiError } from "../api";
import { Badge, Btn, Card, Field, Input, Select, Table, Td, Textarea } from "../ui";
import { LiveSwitch } from "../components/LiveSwitch";

const TEMPLATES = [
    { value: "standard", label: "Standard (text + cards + CTA)" },
    { value: "landing", label: "Landing page" },
    { value: "information", label: "Information + FAQ" },
    { value: "blank", label: "Blank" },
];
const LOCATIONS = [{ value: "header", label: "Header" }, { value: "footer", label: "Footer" },
    { value: "both", label: "Header + Footer" }];

const seoChecks = (page) => {
    const seo = page.seo || {};
    const title = seo.seo_title || page.title || "";
    const desc = seo.meta_description || page.description || "";
    const out = [];
    if (title.length < 20 || title.length > 65) out.push(`SEO title is ${title.length} characters — aim for 20-65`);
    if (desc.length < 70 || desc.length > 165) out.push(`Meta description is ${desc.length} characters — aim for 70-165`);
    if (!/^[a-z0-9-]+$/.test(page.slug)) out.push("Address should only use lowercase letters, numbers and dashes");
    if (seo.noindex) out.push("Noindex is ON — search engines will skip this page");
    if (!(page.draft?.sections || []).some((s) => ["hero", "rich_text"].includes(s.type)))
        out.push("No heading section — add a Hero or Heading & text section so the page has an H1");
    return out;
};

export const PagesManager = ({ onEditLayout, canAdd, canDelete }) => {
    const qc = useQueryClient();
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ name: "", slug: "", title: "", description: "", template: "standard" });
    const [openSeo, setOpenSeo] = useState(null);

    const { data: pages = [] } = useQuery({
        queryKey: ["site-pages"],
        queryFn: async () => (await rhm.get("/site/pages")).data,
    });
    const refresh = () => qc.invalidateQueries({ queryKey: ["site-pages"] });

    const create = useMutation({
        mutationFn: () => rhm.post("/site/pages", form),
        onSuccess: ({ data }) => {
            toast.success(`Page created at /${data.slug}`);
            setCreating(false);
            setForm({ name: "", slug: "", title: "", description: "", template: "standard" });
            refresh();
            onEditLayout(data.slug);
        },
        onError: (e) => toast.error(apiError(e)),
    });
    const patch = useMutation({
        mutationFn: ({ slug, body }) => rhm.patch(`/site/pages/${slug}`, body),
        onSuccess: () => { toast.success("Page updated"); refresh(); },
        onError: (e) => toast.error(apiError(e)),
    });
    const duplicate = useMutation({
        mutationFn: (slug) => rhm.post(`/site/pages/${slug}/duplicate`),
        onSuccess: ({ data }) => { toast.success(`Duplicated to /${data.slug}`); refresh(); },
        onError: (e) => toast.error(apiError(e)),
    });
    const archive = useMutation({
        mutationFn: (slug) => rhm.delete(`/site/pages/${slug}`),
        onSuccess: () => { toast.success("Page archived"); refresh(); },
        onError: (e) => toast.error(apiError(e)),
    });

    const [slugEdits, setSlugEdits] = useState({});
    // Changing an address keeps the old one alive: the backend files a redirect automatically.
    const saveSlug = (slug) => {
        const next = (slugEdits[slug] || "").trim();
        if (!next || next === slug) return;
        patch.mutate({ slug, body: { slug: next } }, {
            onSuccess: () => {
                toast.success(`Now at /${next} — old links redirect here`);
                setSlugEdits((prev) => { const rest = { ...prev }; delete rest[slug]; return rest; });
            },
        });
    };

    const live = pages.filter((p) => !p.is_archived);

    return (
        <div className="space-y-4" data-testid="pages-manager">
            <Card title={`Pages (${live.length})`} action={canAdd && (
                <Btn onClick={() => setCreating((v) => !v)} data-testid="new-page-button">
                    <Plus className="w-3.5 h-3.5 mr-1 inline" /> NEW PAGE
                </Btn>
            )}>
                {creating && (
                    <div className="border border-gold/30 bg-gold/[0.05] p-4 mb-5" data-testid="new-page-form">
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field label="Page name"><Input value={form.name} data-testid="new-page-name"
                                onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                            <Field label="Address" hint="Leave empty to use the name">
                                <Input value={form.slug} data-testid="new-page-slug" placeholder="mens-wellness"
                                    onChange={(e) => setForm({ ...form, slug: e.target.value })} /></Field>
                            <Field label="Browser / SEO title"><Input value={form.title} data-testid="new-page-title"
                                onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
                            <Field label="Template"><Select value={form.template} options={TEMPLATES} data-testid="new-page-template"
                                onChange={(e) => setForm({ ...form, template: e.target.value })} /></Field>
                            <Field label="Meta description" className="md:col-span-2">
                                <Textarea rows={2} value={form.description} data-testid="new-page-description"
                                    onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
                        </div>
                        <div className="mt-4 flex justify-end gap-2">
                            <Btn variant="ghost" onClick={() => setCreating(false)}>CANCEL</Btn>
                            <Btn onClick={() => create.mutate()} data-testid="create-page-confirm">CREATE & EDIT</Btn>
                        </div>
                    </div>
                )}

                <p className="text-[11px] text-zinc-500 mb-4">
                    Edit an address and press SAVE — visitors on the old link are redirected to the new one
                    automatically, and the rule shows up under SEO &amp; Google → Old links.
                </p>
                <Table head={["Page", "Address", "Live", "In menu", "Where", ""]} testId="pages-table">
                    {live.map((p) => (
                        <tr key={p.id} data-testid={`page-row-${p.slug}`}>
                            <Td>{p.name}{p.is_system && <Badge tone="muted">BUILT-IN</Badge>}
                                <div className="text-xs text-zinc-500">{p.title}</div></Td>
                            <Td>
                                {p.slug === "home" || p.is_system ? (
                                    <span className="mono text-[11px] text-gold">/{p.slug === "home" ? "" : p.slug}</span>
                                ) : (
                                    <div className="flex items-center gap-1">
                                        <span className="mono text-[11px] text-zinc-500">/</span>
                                        <Input value={slugEdits[p.slug] ?? p.slug} data-testid={`page-slug-${p.slug}`}
                                            className="!py-1.5 !px-2 mono !text-[11px] w-36"
                                            onChange={(e) => setSlugEdits({ ...slugEdits, [p.slug]: e.target.value })}
                                            onKeyDown={(e) => e.key === "Enter" && saveSlug(p.slug)} />
                                        {(slugEdits[p.slug] ?? p.slug) !== p.slug && (
                                            <Btn variant="ghost" onClick={() => saveSlug(p.slug)}
                                                data-testid={`page-slug-save-${p.slug}`}>SAVE</Btn>
                                        )}
                                    </div>
                                )}
                            </Td>
                            <Td>
                                <LiveSwitch live={!!p.is_published} testId={`page-live-${p.slug}`}
                                    disabled={p.slug === "home"}
                                    previewHref={p.slug === "home" ? "/" : `/${p.slug}`}
                                    onChange={(live) => patch.mutate({ slug: p.slug, body: { is_published: live } })} />
                            </Td>
                            <Td><Select value={p.show_in_menu ? "yes" : "no"} data-testid={`page-menu-${p.slug}`}
                                options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
                                onChange={(e) => patch.mutate({ slug: p.slug, body: { show_in_menu: e.target.value === "yes" } })} /></Td>
                            <Td><Select value={p.menu_location || "header"} options={LOCATIONS} data-testid={`page-location-${p.slug}`}
                                onChange={(e) => patch.mutate({ slug: p.slug, body: { menu_location: e.target.value } })} /></Td>
                            <Td>
                                <div className="flex gap-1 justify-end">
                                    <Btn variant="ghost" onClick={() => onEditLayout(p.slug)} data-testid={`page-edit-${p.slug}`}>
                                        <Pencil className="w-3.5 h-3.5" />
                                    </Btn>
                                    <Btn variant="ghost" onClick={() => setOpenSeo(openSeo === p.slug ? null : p.slug)}
                                        data-testid={`page-seo-${p.slug}`}>SEO</Btn>
                                    {canAdd && <Btn variant="ghost" onClick={() => duplicate.mutate(p.slug)} data-testid={`page-duplicate-${p.slug}`}>
                                        <Copy className="w-3.5 h-3.5" /></Btn>}
                                    {canDelete && !p.is_system && (
                                        <Btn variant="ghost" onClick={() => archive.mutate(p.slug)} data-testid={`page-archive-${p.slug}`}>
                                            <Archive className="w-3.5 h-3.5" /></Btn>
                                    )}
                                </div>
                            </Td>
                        </tr>
                    ))}
                </Table>
            </Card>

            {pages.some((p) => p.is_archived) && (
                <Card title={`Archived pages (${pages.filter((p) => p.is_archived).length})`} data-testid="archived-pages">
                    <ul className="space-y-2">
                        {pages.filter((p) => p.is_archived).map((p) => (
                            <li key={p.id} className="flex items-center justify-between gap-4 border border-white/10 px-4 py-3"
                                data-testid={`archived-row-${p.slug}`}>
                                <span className="text-sm">{p.name} <span className="mono text-[10px] text-zinc-500">/{p.slug}</span></span>
                                <Btn variant="ghost" data-testid={`page-restore-${p.slug}`}
                                    onClick={() => patch.mutate({ slug: p.slug, body: { is_archived: false } })}>RESTORE</Btn>
                            </li>
                        ))}
                    </ul>
                </Card>
            )}

            {openSeo && <SeoPanel slug={openSeo} onSaved={refresh} />}
        </div>
    );
};

const SeoPanel = ({ slug, onSaved }) => {
    const { data: page } = useQuery({
        queryKey: ["site-page", slug],
        queryFn: async () => (await rhm.get(`/site/pages/${slug}`)).data,
    });
    const [seo, setSeo] = useState(null);
    const value = seo || page?.seo || {};
    if (!page) return null;
    const warnings = seoChecks({ ...page, seo: value });

    const save = async () => {
        try {
            await rhm.patch(`/site/pages/${slug}`, { seo: value, title: value.seo_title || page.title });
            toast.success("SEO saved");
            onSaved();
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <Card title={`SEO — /${slug}`} data-testid={`seo-panel-${slug}`}
            action={<Btn onClick={save} data-testid={`seo-save-${slug}`}>SAVE SEO</Btn>}>
            <div className="grid md:grid-cols-2 gap-4">
                <Field label="SEO title" hint={`${(value.seo_title || "").length} characters`}>
                    <Input value={value.seo_title || ""} data-testid="seo-title"
                        onChange={(e) => setSeo({ ...value, seo_title: e.target.value })} /></Field>
                <Field label="Canonical URL"><Input value={value.canonical_url || ""} data-testid="seo-canonical"
                    onChange={(e) => setSeo({ ...value, canonical_url: e.target.value })} /></Field>
                <Field label="Meta description" className="md:col-span-2" hint={`${(value.meta_description || "").length} characters`}>
                    <Textarea rows={2} value={value.meta_description || ""} data-testid="seo-description"
                        onChange={(e) => setSeo({ ...value, meta_description: e.target.value })} /></Field>
                <Field label="Social share image"><Input value={value.social_image || ""} data-testid="seo-social-image"
                    onChange={(e) => setSeo({ ...value, social_image: e.target.value })} /></Field>
                <Field label="Search engines" hint="Noindex hides the page from Google">
                    <Select value={value.noindex ? "noindex" : "index"} data-testid="seo-noindex"
                        options={[{ value: "index", label: "Index (recommended)" }, { value: "noindex", label: "Noindex" }]}
                        onChange={(e) => setSeo({ ...value, noindex: e.target.value === "noindex" })} /></Field>
            </div>
            <div className="mt-5 space-y-2" data-testid="seo-warnings">
                {warnings.length === 0 ? (
                    <div className="flex items-center gap-2 text-sm text-lime">
                        <CheckCircle2 className="w-4 h-4" /> All basic SEO checks pass
                    </div>
                ) : warnings.map((w, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-gold">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {w}
                    </div>
                ))}
            </div>
        </Card>
    );
};
